import { useMemo } from 'react'

import useRequest from '@space-shell/common/hooks/useRequest'
import GlobalSearchService from '@space-shell/common/services/globalSearch/service'

import type { Result, SearchResult } from '@space-shell/common/services/globalSearch/types'
import { EntityType } from '@space-shell/common/services/globalSearch/types'
import type { InfiniteData } from '@tanstack/react-query'

import { MAX_RESULTS_PER_PAGE } from '../constants'
import type { UseGlobalSearchResultsProps } from '../types'

const useGlobalSearchResult = ({
  term,
  enableSearch,
  membership,
  lang
}: UseGlobalSearchResultsProps) => {
  const { data, ...rest } = useRequest(
    ['getGlobalSearch', membership, term],
    () =>
      GlobalSearchService.getGlobalSearch({
        membershipSlug: membership,
        term,
        language: lang,
        size: MAX_RESULTS_PER_PAGE
      }),
    {
      enabled: enableSearch,
      retry: 1
    }
  )

  const parsedData = useMemo<InfiniteData<SearchResult> | undefined>(() => {
    if (!data) return undefined

    const results = data.results.map((result) => {
      const { id, membershipSlug, name, parentId, type, description, productId } = result
      const entityType = type === 'PRODUCT' ? EntityType.MEMBERSHIP : type
      const value = {
        id: Number(id),
        membershipCode: membershipSlug,
        entityId: Number(parentId),
        hashId: id,
        entityType: entityType,
        name,
        description,
        parent: {
          id: Number(parentId)
        },
        parentId: Number(parentId)
      }
      switch (type) {
        case EntityType.PAGE:
        case EntityType.MODULE: {
          const parsedParentId = Number.isNaN(productId) ? Number(parentId) : Number(productId)

          value.parent.id = Number(parsedParentId)
          value.parentId = Number(parsedParentId)
          break
        }

        case EntityType.COMMUNITY:
          value.id = Number(id)
          break

        case EntityType.POST:
          value.id = Number(parentId)
          value.entityId = Number(id)
          break

        default: {
          const parsedParentId = Number.isNaN(id) ? Number(id) : Number(productId)
          value.id = Number(parsedParentId)
          value.entityId = Number(id)
        }
      }

      return value
    }) as Result[]

    return {
      pages: [{ paging: { total: data.results.length, size: data.results.length }, results }],
      pageParams: []
    }
  }, [data])

  return { data: parsedData, ...rest }
}

export default useGlobalSearchResult
